import {concatenateQueryHashWithVariablesHash} from 'src/internal/revamp/queryUtils/queryAndVariablesHashing';

import type {
  FragmentHashWithVariables,
  SubQueryWithMetadata,
} from 'src/internal/revamp/queryUtils/types';

export type RootQueryParams = {
  // Composition document + root variables; multiple mounts can share this key.
  compositionKey: string;
  dedupedSubQueryGroups: ReadonlyArray<SubQueryWithMetadata>;
  deduplicatedIncludeVariables: Record<string, false> | undefined;
  variables: Record<string, unknown>;
};

export type RootQueryParamsMatch = {
  params: RootQueryParams;
  fragments: ReadonlyArray<FragmentHashWithVariables>;
};

/**
 * Assign requested fragments to stored compositions for one root document.
 * Current params arrive newest-rendered first, optionally followed by a retained
 * request snapshot. Return each chosen composition's params with its fragment batch.
 * This selects parameters, not an executor: a render-only match can rebuild a retry
 * but does not prove a committed runner exists to take ownership of that retry.
 */
export default function selectRootQueryRefetches(
  candidates: ReadonlyArray<RootQueryParams>,
  mountedCompositionKeys: ReadonlySet<string>,
  fragments: ReadonlyArray<FragmentHashWithVariables>,
): ReadonlyArray<RootQueryParamsMatch> {
  // Mounted compositions get first choice: their effects have committed and
  // registered them. Newer render-only params may belong to a suspended or
  // abandoned render, so recency alone must not displace a matching mounted root.
  const mounted = candidates.filter(({compositionKey}) =>
    mountedCompositionKeys.has(compositionKey),
  );
  // Search mounted roots first, then fall back only when none matches the requested
  // fragment and variables. Suspended renders store params before effects run, so
  // these render-only entries can be the only matching params for an explicit retry.
  // E.g. a mounted root containing User(limit: 10) cannot serve a User(limit: 20)
  // retry. Recency breaks ties within each tier; a matching mounted root always wins.
  // ObservableStore puts any retained snapshot last and excludes it from the
  // mounted set, so even render-only current params take precedence over it.
  const preferredCandidates = [
    ...mounted,
    ...candidates.filter(
      ({compositionKey}) => !mountedCompositionKeys.has(compositionKey),
    ),
  ];
  // Different compositions can include the same fragment with the same variables.
  // Assign that pair once across the whole batch, including overlapping explicit
  // refetches and invalidations. The same fragment with other variables is separate.
  const assignedFragmentKeys = new Set<string>();
  const matches = new Map<
    string,
    {params: RootQueryParams; fragments: FragmentHashWithVariables[]}
  >();

  for (const fragment of fragments) {
    // Concrete variables request one variant; null invalidates all mounted variants.
    // E.g. User(limit: 10) must not fetch User(limit: 20), but invalidating User can
    // fetch both. requestedKey === null is a wildcard, not a cache-entry key.
    const requestedKey =
      fragment.variables === null
        ? null
        : concatenateQueryHashWithVariablesHash(
            fragment.hash,
            fragment.variables,
          );
    // Broad invalidation must not activate a render-only composition just because
    // its data changed. Only explicit refetches may use the fallback tier.
    const eligible = requestedKey === null ? mounted : preferredCandidates;

    for (const params of eligible) {
      // Match the fragment's resolved variables, not the composition's full root
      // variables. Skipped fragments must not claim a request from another root.
      const groups = params.dedupedSubQueryGroups.filter(
        (group) =>
          !group.skip &&
          group.fragmentHash === fragment.hash &&
          (requestedKey === null ||
            concatenateQueryHashWithVariablesHash(
              group.fragmentHash,
              group.variables,
            ) === requestedKey),
      );

      for (const group of groups) {
        const key = concatenateQueryHashWithVariablesHash(
          group.fragmentHash,
          group.variables,
        );
        if (!assignedFragmentKeys.has(key)) {
          assignedFragmentKeys.add(key);

          // Batch by composition so the caller builds one fetch with this root's
          // variables and the selected fragment include flags.
          const match = matches.get(params.compositionKey) ?? {
            params,
            fragments: [],
          };
          // Resolve an invalidation's wildcard to the selected fragment's variables;
          // the caller must force this specific variant into the fetch.
          match.fragments.push(
            fragment.variables === null
              ? {hash: fragment.hash, variables: group.variables}
              : fragment,
          );
          matches.set(params.compositionKey, match);
        }
      }

      // An explicit refetch needs only the first matching composition, even if an
      // earlier request already claimed the pair. Invalidation keeps looking for
      // other variable variants; assignedFragmentKeys filters out duplicates.
      if (requestedKey !== null && groups.length > 0) {
        break;
      }
    }
  }

  return Array.from(matches.values());
}
